import {
  type AppConfig,
  type AppProcessing,
  accountPath,
  isGoods,
  missingLegalIdentity,
  processingPath,
  publishedContactEmail,
} from "./app-config";

/**
 * Pages legales de l'app, construites a partir de `app.config.ts`. Le texte vit ici,
 * dans le paquet partage, pour que le backend (route `/api/legal`) et le frontend
 * (pages publiques) disent exactement la meme chose.
 *
 * Ce texte est un socle raisonnable, pas un avis juridique : une app qui vend, qui
 * traite des donnees sensibles ou qui s'adresse a des mineurs doit le faire relire.
 * Les seuls textes que l'app redige elle-meme dans ces pages sont ses traitements propres
 * (`legal.processing`), qui s'ajoutent a la politique de confidentialite, et son compte
 * (`legal.account`), qui remplace quatre phrases du template : deux dans la politique de
 * confidentialite (donnees traitees, durees de conservation), une dans les CGU, une sur la page
 * « Mes donnees » (`accountErasure`). Ils s'affichent tels quels (leurs espaces de bordure sont
 * retires, rien d'autre).
 */

export type LegalPageId = "mentions" | "confidentialite" | "cgu" | "cgv";

// Les tableaux ne sont pas `readonly` : ces documents partent tels quels dans une
// reponse JSON validee par `legalDocumentSchema`, et Zod infere des tableaux mutables.
export type LegalSection = {
  readonly heading: string;
  readonly paragraphs: string[];
};

export type LegalDocument = {
  readonly id: LegalPageId;
  /** Adresse publique de la page, servie par le routeur du frontend. */
  readonly path: string;
  readonly title: string;
  /** Date de derniere mise a jour, telle que declaree dans `app.config.ts`. */
  readonly updatedAt: string;
  readonly sections: LegalSection[];
  /** Champs de `app.config.ts` encore vides : la page les affiche en avertissement. */
  readonly missing: string[];
};

/** Valeur renseignee, ou marqueur visible qui dit ou la remplir. */
function field(value: string, path: string): string {
  const trimmed = value.trim();
  return trimmed === "" ? `[a completer dans app.config.ts : ${path}]` : trimmed;
}

/** Mention de transparence exigee par l'article 50 du reglement europeen sur l'IA. */
export const AI_DISCLOSURE =
  "Une partie des contenus de ce service est générée par un système d'intelligence " +
  "artificielle. Ces contenus sont signalés comme tels à l'endroit où ils apparaissent. " +
  "Ils peuvent comporter des erreurs et ne remplacent pas l'avis d'un professionnel.";

/**
 * Encadre sur les garanties legales, impose aux conditions generales de vente de biens par
 * l'article D211-2 du code de la consommation : annexe a cet article, partie A (biens hors
 * animaux domestiques), version en vigueur depuis le 1er octobre 2022, lue sur Legifrance
 * le 5 octobre 2026 (LEGIARTI000045981314). Un paragraphe par alinea.
 *
 * Recopie mot pour mot, sans retouche : ne rien reformuler ni reponctuer. Un test en
 * verifie l'empreinte SHA-256 ; si la loi change, on recopie la nouvelle annexe et on
 * met l'empreinte a jour dans `test/legal.test.ts`.
 */
export const GARANTIE_LEGALE_ENCADRE: string[] = [
  "Le consommateur dispose d'un délai de deux ans à compter de la délivrance du bien pour " +
    "obtenir la mise en œuvre de la garantie légale de conformité en cas d'apparition d'un " +
    "défaut de conformité. Durant ce délai, le consommateur n'est tenu d'établir que " +
    "l'existence du défaut de conformité et non la date d'apparition de celui-ci.",
  "Lorsque le contrat de vente du bien prévoit la fourniture d'un contenu numérique ou d'un " +
    "service numérique de manière continue pendant une durée supérieure à deux ans, la " +
    "garantie légale est applicable à ce contenu numérique ou ce service numérique tout au " +
    "long de la période de fourniture prévue. Durant ce délai, le consommateur n'est tenu " +
    "d'établir que l'existence du défaut de conformité affectant le contenu numérique ou le " +
    "service numérique et non la date d'apparition de celui-ci.",
  "La garantie légale de conformité emporte obligation pour le professionnel, le cas " +
    "échéant, de fournir toutes les mises à jour nécessaires au maintien de la conformité du " +
    "bien.",
  "La garantie légale de conformité donne au consommateur droit à la réparation ou au " +
    "remplacement du bien dans un délai de trente jours suivant sa demande, sans frais et " +
    "sans inconvénient majeur pour lui.",
  "Si le bien est réparé dans le cadre de la garantie légale de conformité, le consommateur " +
    "bénéficie d'une extension de six mois de la garantie initiale.",
  "Si le consommateur demande la réparation du bien, mais que le vendeur impose le " +
    "remplacement, la garantie légale de conformité est renouvelée pour une période de deux " +
    "ans à compter de la date de remplacement du bien.",
  "Le consommateur peut obtenir une réduction du prix d'achat en conservant le bien ou " +
    "mettre fin au contrat en se faisant rembourser intégralement contre restitution du bien, " +
    "si :",
  "1° Le professionnel refuse de réparer ou de remplacer le bien ;",
  "2° La réparation ou le remplacement du bien intervient après un délai de trente jours ;",
  "3° La réparation ou le remplacement du bien occasionne un inconvénient majeur pour le " +
    "consommateur, notamment lorsque le consommateur supporte définitivement les frais de " +
    "reprise ou d'enlèvement du bien non conforme, ou s'il supporte les frais d'installation " +
    "du bien réparé ou de remplacement ;",
  "4° La non-conformité du bien persiste en dépit de la tentative de mise en conformité du " +
    "vendeur restée infructueuse.",
  "Le consommateur a également droit à une réduction du prix du bien ou à la résolution du " +
    "contrat lorsque le défaut de conformité est si grave qu'il justifie que la réduction du " +
    "prix ou la résolution du contrat soit immédiate. Le consommateur n'est alors pas tenu de " +
    "demander la réparation ou le remplacement du bien au préalable.",
  "Le consommateur n'a pas droit à la résolution de la vente si le défaut de conformité est " +
    "mineur.",
  "Toute période d'immobilisation du bien en vue de sa réparation ou de son remplacement " +
    "suspend la garantie qui restait à courir jusqu'à la délivrance du bien remis en état.",
  "Les droits mentionnés ci-dessus résultent de l'application des articles L. 217-1 à L. " +
    "217-32 du code de la consommation.",
  "Le vendeur qui fait obstacle de mauvaise foi à la mise en œuvre de la garantie légale de " +
    "conformité encourt une amende civile d'un montant maximal de 300 000 euros, qui peut " +
    "être porté jusqu'à 10 % du chiffre d'affaires moyen annuel (article L. 241-5 du code de " +
    "la consommation).",
  "Le consommateur bénéficie également de la garantie légale des vices cachés en " +
    "application des articles 1641 à 1649 du code civil, pendant une durée de deux ans à " +
    "compter de la découverte du défaut. Cette garantie donne droit à une réduction de prix " +
    "si le bien est conservé ou à un remboursement intégral contre restitution du bien.",
];

function publisherBlock(config: AppConfig): string[] {
  const { publisher } = config.legal;
  const lines = [
    `Éditeur : ${field(publisher.name, "legal.publisher.name")}.`,
    `Forme juridique : ${field(publisher.legalForm, "legal.publisher.legalForm")}.`,
    `Adresse : ${field(publisher.address, "legal.publisher.address")}.`,
    `Téléphone : ${field(publisher.phone, "legal.publisher.phone")}.`,
    `Adresse électronique : ${publishedContactEmail(config)}.`,
    `Immatriculation : ${field(publisher.registration, "legal.publisher.registration")}.`,
    `Directeur de la publication : ${field(
      publisher.publicationDirector,
      "legal.publisher.publicationDirector",
    )}.`,
  ];
  if (publisher.vatNumber.trim() !== "") {
    lines.push(`Numéro de TVA intracommunautaire : ${publisher.vatNumber.trim()}.`);
  }
  return lines;
}

function hostBlock(config: AppConfig): string[] {
  const { host } = config.legal;
  return [
    `Hébergeur : ${field(host.name, "legal.host.name")}.`,
    `Adresse : ${field(host.address, "legal.host.address")}.`,
    `Téléphone : ${field(host.phone, "legal.host.phone")}.`,
  ];
}

/**
 * Identifiant unique de la filiere des emballages (loi AGEC, article R541-173 du code de
 * l'environnement). Il n'existe que pour une vente de biens et seulement s'il est donne :
 * il figure alors dans les CGV et dans les mentions legales. S'il manque, c'est
 * `missingLegalIdentity` qui le signale a l'ecran.
 */
function packagingIdu(config: AppConfig): string | undefined {
  if (!isGoods(config)) return undefined;
  const idu = config.legal.idu?.trim() ?? "";
  return idu === "" ? undefined : idu;
}

function packagingSection(idu: string): LegalSection {
  return {
    heading: "Emballages",
    paragraphs: [`Identifiant unique de la filière des emballages : ${idu}.`],
  };
}

function mentions(config: AppConfig): LegalSection[] {
  const sections: LegalSection[] = [
    { heading: "Éditeur du service", paragraphs: publisherBlock(config) },
    { heading: "Hébergement", paragraphs: hostBlock(config) },
    {
      heading: "Propriété intellectuelle",
      paragraphs: [
        `Les contenus publiés sur ${config.domain}, hors mentions contraires et hors ` +
          "contenus fournis par les utilisateurs, appartiennent à l'éditeur ou lui ont été " +
          "concédés. " +
          "Leur reproduction ou leur réutilisation suppose son accord écrit préalable.",
      ],
    },
  ];
  const idu = packagingIdu(config);
  if (idu) sections.push(packagingSection(idu));
  if (config.features.llm) {
    sections.push({
      heading: "Contenus générés par intelligence artificielle",
      paragraphs: [AI_DISCLOSURE],
    });
  }
  sections.push({
    heading: "Nous écrire",
    paragraphs: [
      `Pour toute question sur ce service, écrivez à ${publishedContactEmail(config)}.`,
      "Pour signaler un contenu illicite, utilisez la même adresse en décrivant le contenu " +
        "en cause et l'adresse de la page qui le porte.",
    ],
  });
  return sections;
}

/**
 * Les traitements propres a l'app (`legal.processing`) : une ligne par traitement, a la suite
 * de celles du template et dans l'ordre donne. Le texte est celui de l'app, sans ses espaces
 * de bordure et rien de plus. Vide, il laisse le marqueur des autres champs d'identite, qui
 * garde les trois sections alignees (la ligne n d'une section est le traitement n des deux
 * autres) ; `missingLegalIdentity` le liste et la page l'affiche en avertissement.
 */
function appProcessing(config: AppConfig, key: keyof AppProcessing): string[] {
  return (config.legal.processing ?? []).map((entry, index) =>
    field(entry[key], processingPath(index, key)),
  );
}

/**
 * La ligne "Compte" des donnees traitees (option `auth`) : celle que l'app a ecrite
 * (`legal.account.collected`), sinon celle du template, qui decrit la connexion par lien a usage
 * unique. Vide, le texte de l'app laisse le marqueur des champs d'identite, et
 * `missingLegalIdentity` le liste.
 */
function accountCollected(config: AppConfig): string {
  const account = config.legal.account;
  if (account) return field(account.collected, accountPath("collected"));
  return (
    "Compte : votre adresse électronique, la date de création du compte et les dates de " +
    "connexion. Aucun mot de passe n'est conservé : la connexion se fait par lien à " +
    "usage unique envoyé par courrier électronique."
  );
}

/**
 * Le premier paragraphe de la section "Compte" des CGU (option `auth`) : celui que l'app a
 * ecrit (`legal.account.terms`), sinon celui du template. Le second paragraphe de la section,
 * la suppression du compte, n'est jamais remplace.
 */
function accountTerms(config: AppConfig): string {
  const account = config.legal.account;
  if (account) return field(account.terms, accountPath("terms"));
  return (
    "La connexion se fait par lien à usage unique envoyé à votre adresse électronique. " +
    "Vous êtes responsable de l'accès à cette boîte."
  );
}

/**
 * La ligne "Compte" des durees de conservation (option `auth`) : celle que l'app a ecrite
 * (`legal.account.retention`), sinon celle du template, qui annonce `retention.accountDays`
 * jours apres la derniere connexion. Avec le texte de l'app, cette duree-la n'est plus affichee :
 * l'app dit la sienne.
 */
function accountRetention(config: AppConfig): string {
  const account = config.legal.account;
  if (account) return field(account.retention, accountPath("retention"));
  return (
    `Compte : ${config.retention.accountDays} jours après la dernière connexion, puis ` +
    "suppression. Vous pouvez demander la suppression à tout moment."
  );
}

/**
 * La phrase sous les boutons de la page « Mes données » (option `auth`) : ce qu'efface la
 * suppression. Celle que l'app a ecrite (`legal.account.erasure`), sinon celle du template. Vide,
 * le texte de l'app laisse le marqueur des champs d'identite, comme sur les pages legales.
 *
 * Exportee pour cette page, qui n'est pas une page legale mais doit dire la meme chose qu'elles :
 * la regle du marqueur reste ici, au seul endroit qui la connait, et la page ne la recopie pas.
 * Sans l'option `auth`, le bloc ne fait rien, ici non plus : la phrase du template, jamais un
 * marqueur pour un champ que `missingLegalIdentity` ne liste pas.
 */
export function accountErasure(config: AppConfig): string {
  const account = config.features.auth ? config.legal.account : undefined;
  if (account) return field(account.erasure, accountPath("erasure"));
  return "La suppression efface le compte et les données rattachées. Elle est définitive.";
}

function collected(config: AppConfig): string[] {
  const items = [
    "Mesure d'audience : les pages consultées, la date et la source de la visite. Cette " +
      "mesure est interne, sans profilage et sans partage à des fins publicitaires.",
    "Journaux techniques : adresse IP, date et nature de la requête, conservés pour la " +
      "sécurité du service. Les adresses électroniques et les jetons y sont masqués.",
  ];
  if (config.features.auth) {
    items.unshift(accountCollected(config));
  }
  if (config.features.billing) {
    items.push(
      "Paiement : le montant, la date et la référence de la commande. Les données de carte " +
        "sont saisies chez le prestataire de paiement et ne transitent pas par ce service.",
    );
  }
  if (isGoods(config)) {
    items.push(
      "Livraison : nom, adresse postale de livraison et, s'il est donné, téléphone, saisis sur " +
        "la page de paiement.",
    );
  }
  items.push(...appProcessing(config, "collected"));
  return items;
}

function purposes(config: AppConfig): string[] {
  const items = [
    "Fournir le service demandé et répondre aux messages reçus : exécution du contrat ou " +
      "mesures précontractuelles.",
    "Mesurer l'audience du service et en suivre le fonctionnement : intérêt légitime de " +
      "l'éditeur à connaître l'usage de son service.",
    "Assurer la sécurité du service et prévenir les abus : intérêt légitime de l'éditeur.",
  ];
  if (config.features.billing) {
    items.push(
      "Établir et conserver les pièces comptables liées aux commandes : obligation légale.",
    );
  }
  items.push(...appProcessing(config, "purpose"));
  return items;
}

function retention(config: AppConfig): string[] {
  const items = [
    `Mesure d'audience et évènements associés : ${config.retention.analyticsDays} jours.`,
    `Journaux techniques : ${config.retention.logDays} jours.`,
    `Cookie interne de mesure de la source : ${config.retention.attributionDays} jours.`,
  ];
  if (config.features.auth) {
    items.unshift(accountRetention(config));
  }
  if (config.features.billing) {
    items.push(
      "Pièces comptables : dix ans à compter de la clôture de l'exercice, durée imposée par " +
        "le code de commerce.",
    );
  }
  if (isGoods(config)) {
    const days = config.retention.shippingDays ?? 90;
    items.push(
      `Adresse postale de livraison, nom et téléphone : ${days} jours après l'expédition. ` +
        "Adresse électronique liée à une commande : deux ans après l'expédition, durée de la " +
        "garantie légale de conformité.",
    );
  }
  items.push(...appProcessing(config, "retention"));
  return items;
}

function processors(config: AppConfig): string[] {
  const items = [
    `Hébergement et base de données : ${field(config.legal.host.name, "legal.host.name")}.`,
  ];
  if (config.features.auth) {
    items.push(
      "Envoi des courriers électroniques de connexion : le prestataire de messagerie de " +
        "l'éditeur.",
    );
  }
  if (config.features.billing) {
    items.push(
      "Paiement en ligne : Stripe, qui agit comme responsable de traitement pour les données " +
        "de paiement qu'il collecte directement.",
    );
  }
  if (isGoods(config)) {
    items.push(
      "Acheminement des colis : le transporteur choisi par le vendeur, qui reçoit le nom et " +
        "l'adresse de livraison.",
    );
  }
  if (config.features.llm) {
    items.push(
      "Génération de contenu : le fournisseur de modèle utilisé par le service. Le contenu " +
        "que vous soumettez lui est transmis pour produire une réponse.",
    );
  }
  items.push(
    "Aucune donnée n'est vendue ni cédée à des tiers à des fins publicitaires. Les " +
      "conditions de chaque prestataire précisent le lieu de traitement des données.",
  );
  return items;
}

function rights(config: AppConfig): string[] {
  return [
    "Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, " +
      "d'opposition et de portabilité sur les données qui vous concernent.",
    config.features.auth
      ? "Depuis la page « Mes données », vous pouvez exporter vos données au format JSON et " +
        "supprimer votre compte. La suppression est immédiate et définitive."
      : "Ce service ne crée pas de compte utilisateur. Pour exercer vos droits, écrivez à " +
        "l'adresse de contact ci-dessous.",
    `Pour toute demande : ${publishedContactEmail(config)}. Une réponse vous est adressée ` +
      "dans un délai d'un mois.",
    "Si la réponse ne vous convient pas, vous pouvez saisir la Commission nationale de " +
      "l'informatique et des libertés (CNIL), 3 place de Fontenoy, 75007 Paris, www.cnil.fr.",
  ];
}

/**
 * Les paragraphes de la section "Cookies et mesure d'audience". Avec l'option `auth`, la session
 * pose son propre cookie en plus de celui de la mesure : le texte ne dit plus "un seul" et un
 * paragraphe de plus decrit le cookie de session. Sans `auth`, c'est le texte d'avant, a
 * l'octet pres.
 */
function cookies(config: AppConfig): string[] {
  const hasSession = config.features.auth;
  const attribution = hasSession
    ? "Un cookie interne est déposé"
    : "Un seul cookie interne est déposé";
  const paragraphs = [
    "La mesure d'audience de ce service ne dépose pas de cookie et ne suit pas les " +
      "visiteurs d'un site à l'autre.",
    `${attribution}, pour ${config.retention.attributionDays} jours. ` +
      "Il contient un identifiant aléatoire et, le cas échéant, la campagne d'où vient " +
      "la visite. Il sert uniquement à des statistiques internes, n'est lisible que par ce " +
      "service et n'est transmis à personne.",
    "Votre navigateur vous permet de le supprimer à tout moment. Le service continue de " +
      "fonctionner sans lui.",
  ];
  if (hasSession) {
    paragraphs.push(
      "Si vous vous connectez, un cookie de session s'y ajoute : il garde votre connexion " +
        "ouverte, n'est lisible que par ce service et ne sert à rien d'autre. Le supprimer " +
        "vous déconnecte.",
    );
  }
  return paragraphs;
}

function confidentialite(config: AppConfig): LegalSection[] {
  return [
    {
      heading: "Responsable du traitement",
      paragraphs: [
        `Le responsable du traitement est ${field(
          config.legal.publisher.name,
          "legal.publisher.name",
        )}, éditeur de ${config.name}. Ses coordonnées figurent dans les mentions légales.`,
      ],
    },
    { heading: "Données traitées", paragraphs: collected(config) },
    { heading: "Finalités et bases légales", paragraphs: purposes(config) },
    { heading: "Durées de conservation", paragraphs: retention(config) },
    { heading: "Destinataires et sous-traitants", paragraphs: processors(config) },
    { heading: "Cookies et mesure d'audience", paragraphs: cookies(config) },
    { heading: "Vos droits", paragraphs: rights(config) },
  ];
}

function cgu(config: AppConfig): LegalSection[] {
  const sections: LegalSection[] = [
    {
      heading: "Objet",
      paragraphs: [
        `Les présentes conditions encadrent l'utilisation de ${config.name}, accessible à ` +
          `l'adresse ${config.domain}. Utiliser le service vaut acceptation de ces conditions.`,
      ],
    },
    {
      heading: "Accès au service",
      paragraphs: [
        "Le service est accessible en ligne. Son accès peut être interrompu pour maintenance, " +
          "mise à jour ou raison technique, sans préavis.",
        "Les frais de connexion et d'équipement restent à votre charge.",
      ],
    },
  ];
  if (config.features.auth) {
    sections.push({
      heading: "Compte",
      paragraphs: [
        accountTerms(config),
        "Vous pouvez supprimer votre compte à tout moment depuis la page « Mes données ». " +
          "L'éditeur peut fermer un compte en cas de manquement à ces conditions.",
      ],
    });
  }
  sections.push(
    {
      heading: "Usages interdits",
      paragraphs: [
        "Sont interdits : la tentative d'accès non autorisé, l'extraction massive ou " +
          "automatisée des contenus, la mise en défaut volontaire du service, la publication " +
          "de contenus illicites et l'usurpation d'identité.",
      ],
    },
    {
      heading: "Propriété intellectuelle",
      paragraphs: [
        "Le service et ses contenus sont protégés. Vous disposez d'un droit d'usage personnel " +
          "et non exclusif pour la durée de votre utilisation du service.",
      ],
    },
    {
      heading: "Responsabilité",
      paragraphs: [
        "Le service est fourni en l'état. L'éditeur reste responsable dans les conditions " +
          "prévues par la loi, notamment vis-à-vis des consommateurs.",
        "L'éditeur n'est pas responsable des contenus des sites tiers vers lesquels le " +
          "service renvoie.",
      ],
    },
  );
  if (config.features.llm) {
    sections.push({
      heading: "Contenus générés par intelligence artificielle",
      paragraphs: [
        AI_DISCLOSURE,
        "Vérifiez ces contenus avant de les utiliser, en particulier dans un contexte " +
          "professionnel, médical, juridique ou financier.",
      ],
    });
  }
  sections.push(
    {
      heading: "Données personnelles",
      paragraphs: [
        "Le traitement des données personnelles est décrit dans la politique de " +
          "confidentialité, accessible depuis toutes les pages du service.",
      ],
    },
    {
      heading: "Modification des conditions",
      paragraphs: [
        "Ces conditions peuvent être modifiées. La version applicable est celle publiée sur " +
          "le service au jour de son utilisation. Dernière mise à jour : " +
          `${field(config.legal.updatedAt, "legal.updatedAt")}.`,
      ],
    },
    {
      heading: "Droit applicable",
      paragraphs: [
        "Ces conditions sont soumises au droit français. En cas de litige, une solution " +
          "amiable est recherchée avant toute action judiciaire.",
      ],
    },
  );
  return sections;
}

// Sections communes aux CGV numeriques et aux CGV de biens : une seule redaction, donc
// un seul endroit a corriger quand le texte ou la loi change.

function sellerSection(config: AppConfig): LegalSection {
  return {
    heading: "Vendeur",
    paragraphs: [
      ...publisherBlock(config),
      field(config.legal.publisher.vatNotice, "legal.publisher.vatNotice"),
    ],
  };
}

function paymentSection(): LegalSection {
  return {
    heading: "Paiement",
    paragraphs: [
      "Le paiement en ligne est traité par Stripe. Les données de carte sont saisies sur " +
        "les pages du prestataire et ne sont pas conservées par l'éditeur.",
    ],
  };
}

function complaintsSection(config: AppConfig): LegalSection {
  const { mediator } = config.legal;
  return {
    heading: "Réclamation et médiation",
    paragraphs: [
      `Toute réclamation est reçue à l'adresse ${publishedContactEmail(config)}.`,
      "En cas d'échec, le consommateur peut recourir gratuitement au médiateur de la " +
        `consommation : ${field(mediator.name, "legal.mediator.name")}, ` +
        `${field(mediator.address, "legal.mediator.address")}, ` +
        `${field(mediator.url, "legal.mediator.url")}.`,
    ],
  };
}

function governingLawSection(): LegalSection {
  return {
    heading: "Droit applicable",
    paragraphs: [
      "Ces conditions sont soumises au droit français, sans priver le consommateur de la " +
        "protection que lui accorde la loi de son pays de résidence.",
    ],
  };
}

function cgv(config: AppConfig): LegalSection[] {
  if (isGoods(config)) return cgvGoods(config);
  return [
    sellerSection(config),
    {
      heading: "Produits et prix",
      paragraphs: [
        "Les caractéristiques et le prix de chaque offre sont indiqués sur la page de " +
          "commande, avant validation. Les prix sont exprimés en euros.",
        "L'éditeur peut modifier ses prix. Le prix applicable est celui affiché au moment " +
          "de la commande.",
      ],
    },
    {
      heading: "Commande",
      paragraphs: [
        "La commande est ferme lorsque le paiement est accepté. Un courrier électronique de " +
          "confirmation récapitule la commande et son prix.",
      ],
    },
    paymentSection(),
    {
      heading: "Fourniture",
      paragraphs: [
        "L'accès au service commandé est ouvert dès l'acceptation du paiement, sauf mention " +
          "contraire sur la page de commande.",
      ],
    },
    {
      heading: "Droit de rétractation",
      paragraphs: [
        "Le consommateur dispose de quatorze jours à compter de la commande pour se " +
          "rétracter, sans motif, en écrivant à l'adresse de contact.",
        "Conformément à l'article L221-28 du code de la consommation, ce droit ne s'applique " +
          "plus au contenu numérique fourni immédiatement lorsque le consommateur a demandé " +
          "cette fourniture avant la fin du délai et a renoncé expressément à son droit de " +
          "rétractation. Cette demande et cette renonciation sont recueillies au moment de " +
          "la commande.",
        "Le remboursement d'une rétractation valablement exercée intervient dans les quatorze " +
          "jours, par le même moyen de paiement.",
      ],
    },
    {
      heading: "Garanties légales",
      paragraphs: [
        "Le consommateur bénéficie de la garantie légale de conformité, prévue aux articles " +
          "L217-3 et suivants du code de la consommation, et de la garantie contre les vices " +
          "cachés, prévue aux articles 1641 et suivants du code civil.",
      ],
    },
    complaintsSection(config),
    governingLawSection(),
  ];
}

/**
 * CGV d'une app qui vend des biens expedies (`legal.sales = "goods"`) : livraison, droit de
 * retractation a compter de la reception avec son exception pour le sur mesure, frais de
 * retour, encadre officiel des garanties legales et identifiant unique des emballages. Les
 * sections Vendeur, Paiement, Reclamation et mediation, Droit applicable sont partagees avec
 * `cgv` (`sellerSection`, `paymentSection`, `complaintsSection`, `governingLawSection`).
 */
function cgvGoods(config: AppConfig): LegalSection[] {
  const sections: LegalSection[] = [
    sellerSection(config),
    {
      heading: "Produits et prix",
      paragraphs: [
        "Les produits sont fabriqués par le vendeur. Leurs caractéristiques, leurs photos et leur " +
          "prix sont présentés sur leur fiche avant la commande. Les prix sont exprimés en euros, " +
          "toutes taxes comprises, livraison comprise.",
        "Le prix applicable est celui affiché au moment de la commande.",
      ],
    },
    {
      heading: "Commande",
      paragraphs: [
        "La commande est ferme lorsque le paiement est accepté. Un courrier électronique de " +
          "confirmation récapitule la commande, son prix et le lien de suivi.",
      ],
    },
    paymentSection(),
    {
      heading: "Livraison",
      paragraphs: [
        "Les produits sont livrés en France métropolitaine, à l'adresse saisie lors du paiement. " +
          "Le délai de fabrication est indiqué sur chaque fiche ; le numéro de suivi est envoyé " +
          "par courrier électronique à l'expédition.",
      ],
    },
    {
      heading: "Droit de rétractation",
      paragraphs: [
        "Le consommateur dispose de quatorze jours à compter de la réception du produit, ou du " +
          "dernier produit si la commande est livrée en plusieurs fois, pour se rétracter, sans " +
          "motif, par la fonction de rétractation de la page de suivi de sa " +
          `commande ou en écrivant à ${publishedContactEmail(config)}. Un accusé de réception ` +
          "lui est envoyé par courrier électronique.",
        "Conformément à l'article L221-28 du code de la consommation, ce droit ne s'applique pas " +
          "aux produits confectionnés selon les spécifications du consommateur ou nettement " +
          "personnalisés, comme les pièces réalisées sur mesure d'après une photo.",
        "Le produit est renvoyé dans les quatorze jours suivant la rétractation ; les frais de " +
          "retour sont à la charge du consommateur. Le remboursement intervient dans les " +
          "quatorze jours suivant la rétractation, par le même moyen de paiement ; il peut être " +
          "différé jusqu'à la récupération du produit ou la preuve de son expédition.",
      ],
    },
    {
      heading: "Garanties légales",
      paragraphs: [...GARANTIE_LEGALE_ENCADRE],
    },
    complaintsSection(config),
  ];
  const idu = packagingIdu(config);
  if (idu) sections.push(packagingSection(idu));
  sections.push(governingLawSection());
  return sections;
}

const PAGES: Record<LegalPageId, { title: string; path: string }> = {
  mentions: { title: "Mentions légales", path: "/mentions-legales" },
  confidentialite: { title: "Politique de confidentialité", path: "/confidentialite" },
  cgu: { title: "Conditions générales d'utilisation", path: "/cgu" },
  cgv: { title: "Conditions générales de vente", path: "/cgv" },
};

function sectionsFor(config: AppConfig, id: LegalPageId): LegalSection[] {
  if (id === "mentions") return mentions(config);
  if (id === "confidentialite") return confidentialite(config);
  if (id === "cgu") return cgu(config);
  return cgv(config);
}

/** Pages a publier, dans l'ordre d'affichage. Les CGV n'existent que si l'app vend. */
export function legalPageIds(config: AppConfig): LegalPageId[] {
  const ids: LegalPageId[] = ["mentions", "confidentialite", "cgu"];
  if (config.features.billing) ids.push("cgv");
  return ids;
}

/** Une page legale, ou `undefined` si elle ne concerne pas cette app. */
export function legalDocument(config: AppConfig, id: LegalPageId): LegalDocument | undefined {
  if (!legalPageIds(config).includes(id)) return undefined;
  const meta = PAGES[id];
  return {
    id,
    path: meta.path,
    title: meta.title,
    updatedAt: config.legal.updatedAt,
    sections: sectionsFor(config, id),
    missing: missingLegalIdentity(config),
  };
}

/** Toutes les pages legales de l'app. */
export function legalDocuments(config: AppConfig): LegalDocument[] {
  const documents: LegalDocument[] = [];
  for (const id of legalPageIds(config)) {
    const document = legalDocument(config, id);
    if (document) documents.push(document);
  }
  return documents;
}
